import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Megaphone, Clock, CalendarBlank, UsersThree, ForkKnife, VideoCamera,
  CloudSun, Newspaper, House, MapPin, Crown, Cake, Confetti, Sparkle,
  Bell, ShieldCheck, Sun, Moon, LockSimple,
} from '@phosphor-icons/react';
import { apiOrigin } from '@/utils/apiBase';

const API_BASE = apiOrigin();

const RELATION_COLORS = {
  Self: '#FF6B35', Spouse: '#EC4899', Father: '#3B82F6', Mother: '#A855F7',
  Son: '#22C55E', Daughter: '#F59E0B', Brother: '#00E5FF', Sister: '#F472B6',
  Grandfather: '#8B5CF6', Grandmother: '#D946EF',
};

/**
 * Living Room Hub — ambient TV display.
 * Replaces the sparse old HomeDisplay with a rich, always-alive layout:
 *   - Hero (clock, weather, family-name, who's home)
 *   - Countdown card (Dira's birthday, party countdown)
 *   - Family Tree strip (avatars with home/away dot)
 *   - Today's plan (meal, reminders, announcements)
 *   - Camera strip
 *   - News ticker
 *
 * Auto night mode: 22:00 – 06:00 → switches to ambient clock.
 */
export default function LivingRoomHub({ data, currentTime, newsHeadlines, kioskLocked, onUnlockTap }) {
  const hour = currentTime.getHours();
  const isNight = hour >= 22 || hour < 6;
  const [forceDay, setForceDay] = useState(false);
  const wakeTimer = useRef(null);

  const showNight = isNight && !forceDay;

  const handleWake = () => {
    if (!showNight) return;
    setForceDay(true);
    if (wakeTimer.current) clearTimeout(wakeTimer.current);
    wakeTimer.current = setTimeout(() => setForceDay(false), 30000);
  };
  useEffect(() => () => { if (wakeTimer.current) clearTimeout(wakeTimer.current); }, []);

  if (!data) return (
    <div className="fixed inset-0 bg-[#040404] flex items-center justify-center" data-testid="hub-loading">
      <div className="w-8 h-8 border-2 border-[#FF6B35] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (showNight) return <AmbientClock currentTime={currentTime} onTap={handleWake} familyName={data.family_name} />;

  return (
    <div className="fixed inset-0 overflow-hidden text-white select-none"
      data-testid="living-room-hub"
      style={{
        background: 'radial-gradient(ellipse at top, #14110D 0%, #050505 70%)',
        fontFamily: "'Outfit', 'Manrope', sans-serif",
      }}
      onClick={kioskLocked ? onUnlockTap : undefined}>

      {/* Subtle ambient glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-[0.06]"
          style={{ background: 'radial-gradient(circle, #FF6B35, transparent 70%)', filter: 'blur(40px)' }} />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full opacity-[0.04]"
          style={{ background: 'radial-gradient(circle, #00E5FF, transparent 70%)', filter: 'blur(40px)' }} />
      </div>

      <div className="relative z-10 h-full flex flex-col p-6 lg:p-10 gap-5 overflow-hidden">

        {/* Top hero: clock + weather + welcome */}
        <Hero data={data} currentTime={currentTime} />

        {/* Body: 3 columns */}
        <div className="flex-1 grid grid-cols-12 gap-5 min-h-0">

          {/* LEFT — Family + Countdown */}
          <div className="col-span-4 flex flex-col gap-5 min-h-0">
            <CountdownCard countdown={data.countdown || []} />
            <FamilyStrip family={data.family || []} homeCount={data.home_count || 0} />
          </div>

          {/* CENTER — Today's plan */}
          <div className="col-span-5 flex flex-col gap-5 min-h-0">
            <MealCard meal={data.meal} />
            <RemindersCard reminders={data.today_reminders || []} events={data.events || []} />
          </div>

          {/* RIGHT — Announcements + Camera */}
          <div className="col-span-3 flex flex-col gap-5 min-h-0">
            <AnnouncementsCard announcements={data.announcements || []} />
            <CameraCard cameras={data.cameras || []} />
          </div>
        </div>

        {/* Bottom news ticker */}
        {newsHeadlines && newsHeadlines.length > 0 && <NewsTicker headlines={newsHeadlines} />}
      </div>

      {/* Kiosk lock overlay hint */}
      {kioskLocked && (
        <div className="absolute top-6 right-6 z-30">
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
            style={{ background: 'rgba(255,107,53,0.15)', border: '1px solid rgba(255,107,53,0.3)', color: '#FF6B35' }}>
            <LockSimple size={11} weight="fill" /> Kiosk locked
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// HERO — Big clock + weather + greeting
// ============================================================================
function Hero({ data, currentTime }) {
  const hour = currentTime.getHours();
  const greeting = hour < 5 ? 'Good Night' : hour < 12 ? 'Good Morning' : hour < 17 ? 'Good Afternoon' : hour < 21 ? 'Good Evening' : 'Good Night';
  const Icon = hour >= 18 || hour < 6 ? Moon : Sun;

  return (
    <div className="rounded-3xl p-6 lg:p-7 flex items-center justify-between" data-testid="hub-hero"
      style={{
        background: 'linear-gradient(120deg, rgba(255,107,53,0.10) 0%, rgba(255,107,53,0.02) 70%)',
        border: '1px solid rgba(255,107,53,0.18)',
      }}>
      <div className="flex items-center gap-5">
        <div className="w-14 h-14 rounded-2xl flex items-center justify-center"
          style={{ background: 'linear-gradient(135deg, #FF6B35, #FF8E53)', boxShadow: '0 8px 28px rgba(255,107,53,0.45)' }}>
          <Icon size={28} weight="duotone" className="text-white" />
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#FF6B35] font-bold">
            {greeting}, {data.family_name || 'Family'}
          </p>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight mt-1">
            {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
          </h1>
          {data.weather && (
            <div className="flex items-center gap-2 mt-2 text-sm text-neutral-300">
              <CloudSun size={16} weight="duotone" className="text-[#F59E0B]" />
              {data.weather.city} · {data.weather.temp}°C · {data.weather.description}
            </div>
          )}
        </div>
      </div>
      <div className="text-right">
        <p className="text-7xl lg:text-8xl font-bold tracking-tighter tabular-nums leading-none"
          style={{ textShadow: '0 0 40px rgba(255,107,53,0.25)' }}
          data-testid="hub-clock">
          {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
        </p>
        <p className="text-xs text-neutral-500 mt-1.5 tabular-nums">
          {currentTime.toLocaleTimeString('en-IN', { second: '2-digit' })} sec
        </p>
      </div>
    </div>
  );
}

// ============================================================================
// COUNTDOWN CARD — birthdays, parties, anniversaries (next 14 days)
// ============================================================================
function CountdownCard({ countdown }) {
  if (!countdown || countdown.length === 0) {
    return (
      <Card title="Coming up" icon={<Cake size={14} weight="duotone" className="text-[#EC4899]" />} testid="hub-countdown" minH={140}>
        <div className="flex items-center justify-center h-full text-neutral-600 text-xs">
          No birthdays or parties in the next 2 weeks
        </div>
      </Card>
    );
  }
  const next = countdown[0];
  const dayLabel = next.days === 0 ? 'Today!' : next.days === 1 ? 'Tomorrow' : `in ${next.days} days`;
  const accent = next.type === 'birthday' ? '#EC4899' : '#FF6B35';

  return (
    <Card title="Coming up" icon={<Confetti size={14} weight="duotone" style={{ color: accent }} />} testid="hub-countdown" minH={170}>
      <div className="text-center">
        <div className="text-5xl mb-1">{next.emoji}</div>
        <h2 className="text-xl font-bold leading-tight mb-0.5">{next.title}</h2>
        <p className="text-[11px] text-neutral-400 mb-2 truncate">{next.subtitle}</p>
        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider"
          style={{ background: `${accent}20`, color: accent, border: `1px solid ${accent}40` }}>
          {dayLabel}
        </div>
      </div>
      {countdown.length > 1 && (
        <div className="mt-3 pt-3 border-t border-white/5 space-y-1">
          {countdown.slice(1, 3).map((c, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px] text-neutral-400">
              <span>{c.emoji}</span>
              <span className="flex-1 truncate">{c.title}</span>
              <span className="text-neutral-600">{c.days}d</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ============================================================================
// FAMILY STRIP — Avatar grid with home/away dots
// ============================================================================
function FamilyStrip({ family, homeCount }) {
  if (family.length === 0) return null;

  return (
    <Card
      title="Family"
      subtitle={`${homeCount} home · ${family.length - homeCount} away`}
      icon={<UsersThree size={14} weight="duotone" className="text-[#FF6B35]" />}
      testid="hub-family"
      flex>
      <div className="grid grid-cols-3 gap-3">
        {family.slice(0, 9).map(m => {
          const c = RELATION_COLORS[m.relation] || '#737373';
          const ini = (m.name?.[0] || '?').toUpperCase();
          return (
            <div key={m.member_id} className="text-center" data-testid={`hub-family-${m.member_id}`}>
              <div className="relative inline-block">
                {m.avatar ? (
                  <img src={m.avatar} alt={m.name}
                    className="w-12 h-12 rounded-full object-cover"
                    style={{ border: `2px solid ${c}50` }} />
                ) : (
                  <div className="w-12 h-12 rounded-full flex items-center justify-center font-bold text-base"
                    style={{
                      background: `linear-gradient(135deg, ${c}30, ${c}15)`,
                      border: `2px solid ${c}50`,
                      color: c,
                    }}>{ini}</div>
                )}
                <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full"
                  style={{
                    background: m.is_home ? '#22C55E' : '#525252',
                    border: '2px solid #050505',
                    boxShadow: m.is_home ? '0 0 8px rgba(34,197,94,0.6)' : 'none',
                  }} />
              </div>
              <p className="text-[10px] font-semibold mt-1 truncate">{m.name}</p>
              <p className="text-[9px] uppercase tracking-wider" style={{ color: c }}>{m.relation || 'Member'}</p>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ============================================================================
// MEAL CARD
// ============================================================================
function MealCard({ meal }) {
  const items = meal ? [
    { label: 'Breakfast', value: meal.breakfast },
    { label: 'Lunch', value: meal.lunch },
    { label: 'Snacks', value: meal.snacks },
    { label: 'Dinner', value: meal.dinner },
  ].filter(x => x.value) : [];

  if (items.length === 0) {
    return (
      <Card title="Today's Meals" icon={<ForkKnife size={14} weight="duotone" className="text-[#22C55E]" />} testid="hub-meal">
        <p className="text-xs text-neutral-500 italic text-center py-4">No meal plan set for today</p>
      </Card>
    );
  }

  return (
    <Card title="Today's Meals" icon={<ForkKnife size={14} weight="duotone" className="text-[#22C55E]" />} testid="hub-meal">
      <div className="grid grid-cols-2 gap-3">
        {items.map((it, i) => (
          <div key={i} className="rounded-2xl p-3"
            style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.15)' }}>
            <p className="text-[10px] uppercase tracking-widest text-[#22C55E] font-bold mb-1">{it.label}</p>
            <p className="text-sm font-semibold text-white leading-tight line-clamp-2">{it.value}</p>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ============================================================================
// REMINDERS CARD
// ============================================================================
function RemindersCard({ reminders, events }) {
  const items = [
    ...events.map(e => ({ time: e.start_time || '—', title: e.title, kind: 'event' })),
    ...reminders.map(r => ({ time: r.trigger_time || '—', title: r.title, kind: 'reminder', priority: r.priority })),
  ];
  items.sort((a, b) => (a.time || '').localeCompare(b.time || ''));

  if (items.length === 0) {
    return (
      <Card title="Today's Plan" icon={<CalendarBlank size={14} weight="duotone" className="text-[#3B82F6]" />} testid="hub-plan" flex>
        <div className="flex flex-col items-center justify-center h-full text-center text-neutral-500">
          <CalendarBlank size={28} weight="duotone" className="text-neutral-700 mb-2" />
          <p className="text-xs">Nothing scheduled for today</p>
          <p className="text-[10px] text-neutral-700 mt-1">A relaxed day ahead</p>
        </div>
      </Card>
    );
  }

  return (
    <Card title="Today's Plan" subtitle={`${items.length} item${items.length > 1 ? 's' : ''}`}
      icon={<CalendarBlank size={14} weight="duotone" className="text-[#3B82F6]" />} testid="hub-plan" flex>
      <div className="space-y-2 overflow-y-auto pr-1 max-h-full">
        {items.slice(0, 6).map((it, i) => {
          const c = it.kind === 'event' ? '#3B82F6' : (it.priority === 'high' ? '#EF4444' : '#F59E0B');
          return (
            <div key={i} className="flex items-center gap-3 rounded-xl px-3 py-2"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <div className="w-12 text-center flex-shrink-0">
                <p className="text-xs font-bold tabular-nums" style={{ color: c }}>{it.time}</p>
              </div>
              <div className="w-0.5 h-8 rounded-full" style={{ background: c }} />
              <p className="text-sm flex-1 truncate text-white">{it.title}</p>
              {it.kind === 'reminder' && <Bell size={12} className="text-neutral-600" />}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ============================================================================
// ANNOUNCEMENTS CARD
// ============================================================================
function AnnouncementsCard({ announcements }) {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (announcements.length <= 1) return;
    const t = setInterval(() => setIdx(i => (i + 1) % announcements.length), 6000);
    return () => clearInterval(t);
  }, [announcements.length]);

  if (announcements.length === 0) {
    return (
      <Card title="Announcements" icon={<Megaphone size={14} weight="duotone" className="text-[#FF6B35]" />} testid="hub-announcements" flex>
        <p className="text-xs text-neutral-500 italic text-center py-4">No announcements</p>
      </Card>
    );
  }

  const ann = announcements[idx];
  const TYPE_COLOR = { urgent: '#EF4444', reminder: '#F59E0B', info: '#3B82F6', success: '#22C55E' };
  const c = TYPE_COLOR[ann.type] || '#FF6B35';

  return (
    <Card title="Announcements" subtitle={`${idx + 1} of ${announcements.length}`}
      icon={<Megaphone size={14} weight="duotone" style={{ color: c }} />}
      testid="hub-announcements" flex>
      <div className="rounded-2xl p-4 h-full flex flex-col" style={{ background: `${c}10`, border: `1px solid ${c}30` }}>
        <div className="text-[9px] uppercase tracking-widest font-bold mb-1.5" style={{ color: c }}>{ann.type || 'Notice'}</div>
        <h3 className="text-base font-bold leading-tight mb-1">{ann.title}</h3>
        <p className="text-xs text-neutral-300 line-clamp-4 flex-1">{ann.message}</p>
        {announcements.length > 1 && (
          <div className="flex gap-1 mt-2">
            {announcements.map((_, i) => (
              <span key={i} className="h-0.5 flex-1 rounded-full transition-colors"
                style={{ background: i === idx ? c : 'rgba(255,255,255,0.1)' }} />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

// ============================================================================
// CAMERA CARD
// ============================================================================
function CameraCard({ cameras }) {
  if (cameras.length === 0) {
    return (
      <Card title="Cameras" icon={<VideoCamera size={14} weight="duotone" className="text-[#00E5FF]" />} testid="hub-cameras">
        <p className="text-[10px] text-neutral-500 italic text-center py-3">No cameras configured</p>
      </Card>
    );
  }
  return (
    <Card title="Live View" subtitle={`${cameras.length} cam${cameras.length > 1 ? 's' : ''}`}
      icon={<VideoCamera size={14} weight="duotone" className="text-[#00E5FF]" />}
      testid="hub-cameras">
      <div className="grid grid-cols-1 gap-2">
        {cameras.slice(0, 2).map(cam => (
          <div key={cam.camera_id}
            className="aspect-video rounded-xl flex items-center justify-center relative overflow-hidden"
            style={{ background: 'linear-gradient(135deg, rgba(0,229,255,0.08), rgba(0,229,255,0.02))', border: '1px solid rgba(0,229,255,0.15)' }}>
            <VideoCamera size={20} weight="duotone" className="text-[#00E5FF] opacity-60" />
            <div className="absolute top-1.5 left-1.5 flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] uppercase tracking-wider font-bold"
              style={{ background: 'rgba(0,0,0,0.6)' }}>
              <span className="w-1 h-1 rounded-full bg-red-500 animate-pulse" /> Live
            </div>
            <div className="absolute bottom-1.5 left-1.5 right-1.5">
              <p className="text-[10px] font-semibold text-white truncate">{cam.name}</p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ============================================================================
// NEWS TICKER
// ============================================================================
function NewsTicker({ headlines }) {
  return (
    <div className="rounded-2xl px-5 py-3 flex items-center gap-3 overflow-hidden flex-shrink-0"
      data-testid="hub-news-ticker"
      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
      <Newspaper size={14} weight="duotone" className="text-[#FF6B35] flex-shrink-0" />
      <div className="flex-1 overflow-hidden">
        <div className="flex gap-12 whitespace-nowrap text-xs text-neutral-300 ticker-scroll">
          {headlines.concat(headlines).map((h, i) => (
            <span key={i} className="flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[#FF6B35]" />
              {h.title || h}
            </span>
          ))}
        </div>
      </div>
      <style>{`
        @keyframes ticker {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .ticker-scroll { animation: ticker 60s linear infinite; }
      `}</style>
    </div>
  );
}

// ============================================================================
// AMBIENT NIGHT CLOCK (10pm – 6am)
// ============================================================================
function AmbientClock({ currentTime, onTap, familyName }) {
  return (
    <div className="fixed inset-0 bg-[#030303] text-white select-none cursor-pointer flex flex-col items-center justify-center"
      data-testid="hub-ambient-night" onClick={onTap}>
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full opacity-[0.02]"
          style={{ background: 'radial-gradient(circle, #FF6B35 0%, transparent 70%)' }} />
      </div>
      <Moon size={28} weight="duotone" className="text-neutral-700 mb-6" />
      <p className="text-[120px] font-bold tracking-tighter tabular-nums text-neutral-300 leading-none"
        style={{ textShadow: '0 0 80px rgba(255,107,53,0.06)' }}>
        {currentTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
      </p>
      <p className="text-sm text-neutral-600 mt-3">
        {currentTime.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}
      </p>
      <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-700 mt-12">
        Sleep well, {familyName || 'family'}
      </p>
      <p className="text-[9px] uppercase tracking-[0.25em] text-neutral-800 mt-2">
        Tap to wake
      </p>
    </div>
  );
}

// ============================================================================
// Reusable Card primitive
// ============================================================================
function Card({ title, subtitle, icon, children, testid, flex = false, minH = null }) {
  return (
    <div className={`rounded-3xl p-4 lg:p-5 ${flex ? 'flex-1 flex flex-col min-h-0' : ''}`}
      data-testid={testid}
      style={{
        background: 'rgba(255,255,255,0.025)',
        border: '1px solid rgba(255,255,255,0.06)',
        backdropFilter: 'blur(20px)',
        minHeight: minH ? `${minH}px` : undefined,
      }}>
      <div className="flex items-center justify-between mb-3 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          {icon}
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-neutral-400">{title}</span>
        </div>
        {subtitle && <span className="text-[10px] text-neutral-600">{subtitle}</span>}
      </div>
      <div className={flex ? 'flex-1 min-h-0' : ''}>
        {children}
      </div>
    </div>
  );
}
